import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Domestic = () => {
  const [flights, setFlights] = useState([]);
  const [internationalFlights, setInternationalFlights] = useState([]); // 国際線データ
  const [loading, setLoading] = useState(false); // 初期状態をfalseに変更
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null); // 最終更新日時を追加
  const [expandedStations, setExpandedStations] = useState({
    station1: true,
    station2: true,
    station3: true,
    station4: true
  });

  // 日本時間フォーマット関数
  const formatJapanTime = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };

  // 既存データを取得する関数（スクレイピングなし）
  const fetchExistingData = async () => {
    setLoading(true);
    setError(null);
    try {
      // 国内線の既存データを取得
      const domesticController = new AbortController();
      const domesticTimeoutId = setTimeout(() => domesticController.abort(), 30000);
      const domesticRes = await fetch('https://haneda-domestic-api-746632636311.asia-northeast1.run.app/flights/domestic?with_international_t2=true', {
        signal: domesticController.signal,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        cache: 'no-cache'
      });
      clearTimeout(domesticTimeoutId);
      if (!domesticRes.ok) throw new Error(`国内線: HTTP ${domesticRes.status}`);
      const domesticData = await domesticRes.json();
      setFlights(domesticData);

      // 国際線の既存データを取得
      const intlController = new AbortController();
      const intlTimeoutId = setTimeout(() => intlController.abort(), 30000);
      const intlRes = await fetch('https://haneda-international-api-746632636311.asia-northeast1.run.app/flights/international', {
        signal: intlController.signal,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        cache: 'no-cache'
      });
      clearTimeout(intlTimeoutId);
      if (!intlRes.ok) throw new Error(`国際線: HTTP ${intlRes.status}`);
      const intlData = await intlRes.json();
      setInternationalFlights(intlData);
      
      // 既存データ取得時の更新日時を記録
      const now = new Date();
      console.log('Setting lastUpdated:', now);
      setLastUpdated(now);
    } catch (err) {
      setError(`データ取得に失敗: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // スクレイピング実行とデータ更新を行う関数
  const updateData = async () => {
    setLoading(true);
    setError(null);
    try {
      // 国内線スクレイピング実行
      const domesticScrapeResponse = await fetch('https://haneda-domestic-api-746632636311.asia-northeast1.run.app/scrape/domestic', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (!domesticScrapeResponse.ok) throw new Error('国内線スクレイピングに失敗しました');

      // 国際線スクレイピング実行
      const intlScrapeResponse = await fetch('https://haneda-international-api-746632636311.asia-northeast1.run.app/scrape/international', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (!intlScrapeResponse.ok) throw new Error('国際線スクレイピングに失敗しました');

      // 更新されたデータを取得
      const timestamp = new Date().getTime();
      
      // 国内線データ取得
      const domesticDataResponse = await fetch(`https://haneda-domestic-api-746632636311.asia-northeast1.run.app/flights/domestic?with_international_t2=true&t=${timestamp}`);
      if (!domesticDataResponse.ok) throw new Error('国内線データ取得に失敗しました');
      const domesticData = await domesticDataResponse.json();
      setFlights(domesticData);

      // 国際線データ取得
      const intlDataResponse = await fetch(`https://haneda-international-api-746632636311.asia-northeast1.run.app/flights/international?t=${timestamp}`);
      if (!intlDataResponse.ok) throw new Error('国際線データ取得に失敗しました');
      const intlData = await intlDataResponse.json();
      setInternationalFlights(intlData);

      // 更新日時を記録
      const now = new Date();
      console.log('Update completed, setting lastUpdated:', now);
      setLastUpdated(now);
      alert('データが最新状態に更新されました');
    } catch (err) {
      setError(`データ更新に失敗: ${err.message}`);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 初回ロード時は既存データのみ取得（スクレイピングなし）
  useEffect(() => {
    fetchExistingData();
  }, []);

  // 乗り場別にフライトを分類
  const categorizeFlights = () => {
    const stations = {
      station1: [], // ターミナル1かつ出口1-4
      station2: [], // ターミナル1かつ出口5-8
      station3: [], // ターミナル2かつ出口1-3
      station4: [], // ターミナル2かつ出口4-6 + 国際線T2
    };

    flights.forEach(flight => {
      const terminal = flight.terminal?.toString().replace('T', '') || '';
      const gate = flight.gate || '';
      const normalizedGate = gate
        .replace(/１/g, '1').replace(/２/g, '2').replace(/３/g, '3')
        .replace(/４/g, '4').replace(/５/g, '5').replace(/６/g, '6')
        .replace(/７/g, '7').replace(/８/g, '8').replace(/９/g, '9')
        .replace(/０/g, '0');
      const gateMatch = normalizedGate.match(/(\d+)/);
      const gateNum = gateMatch ? parseInt(gateMatch[1], 10) : null;
      if (terminal === '1') {
        if (gateNum >= 1 && gateNum <= 4) {
          stations.station1.push({...flight, flightType: 'domestic'});
        } else if (gateNum >= 5 && gateNum <= 8) {
          stations.station2.push({...flight, flightType: 'domestic'});
        }
      } else if (terminal === '2') {
        if (gateNum >= 1 && gateNum <= 3) {
          stations.station3.push({...flight, flightType: 'domestic'});
        } else if (gateNum >= 4 && gateNum <= 6) {
          stations.station4.push({...flight, flightType: 'domestic'});
        }
      }
    });

    // 国際線T2便をstation4に追加
    internationalFlights.forEach(flight => {
      const terminal = flight.terminal?.toString().replace('T', '') || '';
      if (terminal === '2') {
        stations.station4.push({
          ...flight,
          flightType: 'international'
        });
      }
    });

    return stations;
  };

  // 30分毎のフライト数を計算
  const getHalfHourlyFlights = (stationFlights) => {
    const domesticCounts = Array(48).fill(0);
    const internationalCounts = Array(48).fill(0);
    
    stationFlights.forEach(flight => {
      const timeStr = flight.scheduled_time || '';
      if (timeStr) {
        const [hours, minutes] = timeStr.split(':').map(Number);
        if (!isNaN(hours) && !isNaN(minutes)) {
          const index = hours * 2 + (minutes >= 30 ? 1 : 0);
          if (index >= 0 && index < 48) {
            if (flight.flightType === 'international') {
              internationalCounts[index]++;
            } else {
              domesticCounts[index]++;
            }
          }
        }
      }
    });

    const datasets = [];
    
    // 国内線データセット
    if (domesticCounts.some(count => count > 0)) {
      datasets.push({
        label: `国内線 (${stationFlights.filter(f => f.flightType !== 'international').length}便)`,
        data: domesticCounts,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      });
    }
    
    // 国際線データセット
    if (internationalCounts.some(count => count > 0)) {
      datasets.push({
        label: `国際線T2 (${stationFlights.filter(f => f.flightType === 'international').length}便)`,
        data: internationalCounts,
        backgroundColor: 'rgba(255, 105, 180, 0.6)', // ピンク色
        borderColor: 'rgba(255, 105, 180, 1)',
        borderWidth: 1
      });
    }

    return {
      labels: Array(48).fill().map((_, i) => {
        const hour = Math.floor(i / 2);
        const minute = i % 2 === 0 ? '00' : '30';
        return `${hour}:${minute}`;
      }),
      datasets: datasets
    };
  };

  // テーブル用ソート関数（変更時刻→予定時刻の順で昇順）
  const sortFlightsByTime = (flights) => {
    return [...flights].sort((a, b) => {
      // 変更時刻があればそちらを優先
      const getTime = (flight) => {
        const t = flight.changed_time || flight.scheduled_time || '';
        const m = t.match(/(\d{1,2}):(\d{2})/);
        if (!m) return 9999 * 60; // 不正値は末尾
        return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
      };
      return getTime(a) - getTime(b);
    });
  };

  // 共通ヘッダーコンポーネント
  const PageHeader = () => {
    console.log('PageHeader rendering, lastUpdated:', lastUpdated);
    return (
      <header className="main-header">
        <div className="breadcrumb">
          <Link to="/home">ホーム</Link> {'>'} <span>国内線フライト情報</span>
        </div>
        <h1>羽田空港 国内線フライト情報</h1>
        {lastUpdated && (
          <div className="last-updated">
            最終更新: {formatJapanTime(lastUpdated)}
          </div>
        )}
        <nav className="main-nav">
          <Link to="/home">ホーム</Link>
          <Link to="/domestic" className="active">国内線</Link>
          <Link to="/international">国際線</Link>
        </nav>
      </header>
    );
  };

  // ローディング表示
  if (loading) return (
    <div className="app-container">
      <PageHeader />
      <div className="container" style={{ textAlign: 'center', padding: '50px' }}>
        <div className="loading">データを読み込み中...</div>
      </div>
    </div>
  );

  // エラー表示
  if (error) return (
    <div className="app-container">
      <PageHeader />
      <div className="container" style={{ textAlign: 'center', padding: '50px' }}>
        <div className="error" style={{ color: 'red' }}>エラー: {error}</div>
      </div>
    </div>
  );

  // データがない場合
  if (flights.length === 0) return (
    <div className="app-container">
      <PageHeader />
      <div className="container" style={{ textAlign: 'center', padding: '50px' }}>
        <p>表示できるデータがありません。</p>
      </div>
    </div>
  );

  const stationFlights = categorizeFlights();
  const stationNames = {
    station1: '1号乗り場 (T1 出口1-4)',
    station2: '2号乗り場 (T1 出口5-8)',
    station3: '3号乗り場 (T2 出口1-3)',
    station4: '4号乗り場 (T2 出口4-6 + 国際線T2)'
  };

  // 乗り場セクションを表示
  const renderStationSection = (stationKey) => {
    const stationData = stationFlights[stationKey];
    const isExpanded = expandedStations[stationKey];

    return (
      <div className="station-section" key={stationKey}>
        <h3 onClick={() => setExpandedStations({...expandedStations, [stationKey]: !isExpanded})}>
          {stationNames[stationKey]} ({stationData.length}便)
          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
            {isExpanded ? '▼' : '▶'}
          </span>
        </h3>
        
        <div className="chart-container" style={{ height: '300px', marginBottom: '20px' }}>
          <Bar 
            data={getHalfHourlyFlights(stationData)} 
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: { 
                x: {
                  stacked: true
                },
                y: { 
                  beginAtZero: true,
                  stacked: true
                }
              },
              plugins: {
                legend: {
                  display: true,
                  position: 'top'
                }
              }
            }} 
          />
        </div>

        {isExpanded && (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>ステータス</th>
                  <th>予定時刻</th>
                  <th>変更時刻</th>
                  <th>出発地</th>
                  <th>便名</th>
                </tr>
              </thead>
              <tbody>
                {sortFlightsByTime(stationData).map((flight, index) => (
                  <tr key={flight.id || index}>
                    <td>{flight.status || '-'}</td>
                    <td>{flight.scheduled_time || '-'}</td>
                    <td>{flight.changed_time || '-'}</td>
                    <td>{flight.airport || '-'}</td>
                    <td>{flight.flight_number || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="app-container">
      <PageHeader />
      <div className="container">
        <div className="action-bar">
          <h2>国内線フライト情報</h2>
          <div className="action-buttons">
            <button 
              onClick={updateData}
              className="action-button update"
              disabled={loading}
            >
              {loading ? 'データ更新中...' : 'データ更新'}
            </button>
          </div>
        </div>

        {Object.keys(stationFlights).map(stationKey => renderStationSection(stationKey))}
      </div>
    </div>
  );
};

export default Domestic;
